import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  Currency as CurrencyType,
  FiatCurrency,
} from '__generated__/globalTypes';
import { DropdownSelect } from 'atoms/inputs/DropdownSelect';
import { Vertical } from 'atoms/layout/flex';
import { BodyM, BodyS } from 'atoms/typography';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useUpdateCurrency } from 'hooks/useUpdateCurrency';
import { useFiatBalance } from 'hooks/wallets/useFiatBalance';
import { userAttributes } from 'lib/glossary';

import { SettingsSection } from '../SettingsSection';

const Group = styled(Vertical)`
  align-items: flex-start;
`;

const Label = styled(Vertical)`
  padding-right: var(--double-unit);
`;

const messages = defineMessages({
  title: {
    id: 'Settings.currency.title',
    defaultMessage: 'Market currency',
  },
  currencyDetails: {
    id: 'Settings.currency.currencyDetails',
    defaultMessage:
      'Changing your primary currency will update how card prices are displayed. It will not impact the currency preferences in your Cash Wallet or ETH Wallet.',
  },
  primaryCurrency: {
    id: 'Settings.currency.primaryCurrency',
    defaultMessage: 'Primary currency',
  },
  fiatCurrencyDetails: {
    id: 'Settings.currency.fiatCurrencyDetails',
    defaultMessage:
      'Choose the currency that you would like to see displayed across Sorare, including your wallet and the marketplace.',
  },
});

export const Currency = () => {
  const { updateCurrency, loading } = useUpdateCurrency();
  const {
    currentUser,
    walletPreferences: { showEthWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { canListAndTrade } = useFiatBalance();
  if (!currentUser) return null;
  const { userSettings } = currentUser;

  const { currency, fiatCurrency } = userSettings;

  const currencies = Object.values(CurrencyType).map(v => ({
    label:
      showEthWallet && showFiatWallet && v === CurrencyType.FIAT
        ? fiatCurrency?.toString()
        : v.toString(),
    value: v,
  }));

  const fiatCurrencies = Object.values(FiatCurrency).map(v => ({
    label: v.toString(),
    value: v,
  }));

  if (!showEthWallet && canListAndTrade) return null;

  return (
    <SettingsSection title={messages.title}>
      {!!showEthWallet && (
        <Group>
          <Label>
            <BodyM color="var(--c-white)">
              <FormattedMessage
                {...(canListAndTrade
                  ? messages.primaryCurrency
                  : userAttributes.currency)}
              />
            </BodyM>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage {...messages.currencyDetails} />
            </BodyS>
          </Label>
          <DropdownSelect
            size="small"
            value={currency || undefined}
            onChange={value => {
              const changedCurrency = currencies.find(c => c.value === value);
              if (changedCurrency) {
                updateCurrency('currency')(changedCurrency);
              }
            }}
            options={currencies}
            disabled={loading}
          />
        </Group>
      )}
      {!canListAndTrade && (
        <Group>
          <Label>
            <BodyM color="var(--c-white)">
              <FormattedMessage {...userAttributes.fiatCurrency} />
            </BodyM>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage {...messages.fiatCurrencyDetails} />
            </BodyM>
          </Label>
          <DropdownSelect
            size="small"
            value={fiatCurrency || undefined}
            onChange={value => {
              const changedCurrency = fiatCurrencies.find(
                c => c.value === value
              );
              if (changedCurrency) {
                updateCurrency('fiatCurrency')(changedCurrency);
              }
            }}
            options={fiatCurrencies}
            disabled={loading}
          />
        </Group>
      )}
    </SettingsSection>
  );
};
